// Iter87 / Phase 3G — CFM Inventory Context.
//
// Strictly in-memory React Context that lets the operator
// manually provide the inventory / campaign context required by
// the CFM-CC-SHARES and CFM-CC-LEAPS modules. This is the
// foundation for any future CFM auto-scan panels — Phase 3G
// implements ONLY the context + UI layer; it does NOT call any
// CFM scanner endpoint, does NOT fetch broker / account data,
// does NOT persist anything.
//
// HARD CONSTRAINTS:
//  * In-memory only. No `localStorage` / `sessionStorage` /
//    `IndexedDB` / cookies / URL params / backend persistence.
//  * No outbound HTTP. The provider is a pure state container.
//  * No `useEffect`. Lifetime = React tree only.

import React, {
  createContext, useContext, useMemo, useState,
} from "react";

const CFMInventoryContext = createContext({
  rows: {},
  metadata: null,
  upsertRow: () => {},
  updateField: () => {},
  updateLeapsField: () => {},
  removeRow: () => {},
  clearAll: () => {},
  importFromJson: () => {},
  exportEnvelope: () => null,
});

// ── Shape helpers ───────────────────────────────────────────────

export function defaultSharesPart() {
  return {
    shares_owned: "",
    share_basis: "",
    total_call_credits: "",
    minimum_exit_price: "",
    shares_assignment_policy: "",
    notes: "",
  };
}

export function defaultLeapsPart() {
  return {
    allow_leaps: false,
    existing_leaps_position: {
      underlying_symbol: "",
      expiration: "",
      strike: "",
      option_type: "call",
      quantity: "",
      basis: "",
      delta: "",
      theta: "",
    },
    total_short_call_credits: "",
    leaps_assignment_policy: "",
    campaign_notes: "",
  };
}

export function defaultRow(symbol) {
  return {
    symbol: (symbol || "").toUpperCase(),
    shares: defaultSharesPart(),
    leaps: defaultLeapsPart(),
  };
}

// ── Validation / readiness rules ────────────────────────────────

function numOrNull(s) {
  if (s === "" || s === null || s === undefined) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function validateRow(row) {
  const warnings = [];
  if (!row.symbol || !row.symbol.trim()) {
    warnings.push("Missing symbol.");
  }
  const sharesOwned = numOrNull(row.shares.shares_owned);
  if (row.shares.shares_owned !== "" && (sharesOwned === null || sharesOwned < 0)) {
    warnings.push("Invalid shares_owned.");
  }
  // Soft "incomplete shares context" warnings — only fire when
  // operator has started filling shares fields.
  const startedShares = (
    row.shares.shares_owned !== ""
    || row.shares.share_basis !== ""
    || row.shares.total_call_credits !== ""
    || row.shares.minimum_exit_price !== ""
    || row.shares.shares_assignment_policy !== ""
  );
  if (startedShares && row.shares.share_basis === "") {
    warnings.push("Missing share_basis.");
  }
  if (startedShares && row.shares.shares_assignment_policy === "") {
    warnings.push("Missing assignment_policy (shares).");
  }

  if (row.leaps.allow_leaps) {
    const elp = row.leaps.existing_leaps_position || {};
    const startedLeaps = (
      elp.expiration !== ""
      || elp.strike !== ""
      || elp.basis !== ""
      || elp.quantity !== ""
    );
    if (startedLeaps && elp.basis === "") {
      warnings.push("Missing LEAPS basis.");
    }
    if (startedLeaps && elp.expiration === "") {
      warnings.push("Missing LEAPS expiration.");
    }
    if (row.leaps.leaps_assignment_policy === "") {
      warnings.push("Missing assignment_policy (LEAPS).");
    }
  }
  return warnings;
}

export function isSharesReady(row) {
  const s = row.shares || {};
  return (
    !!row.symbol
    && numOrNull(s.shares_owned) !== null
    && numOrNull(s.shares_owned) > 0
    && numOrNull(s.share_basis) !== null
    && !!s.shares_assignment_policy
  );
}

export function isLeapsReady(row) {
  if (!row.leaps?.allow_leaps) return false;
  const elp = row.leaps.existing_leaps_position || {};
  const hasPosition = (
    !!elp.expiration
    && numOrNull(elp.strike) !== null
    && numOrNull(elp.quantity) !== null
    && numOrNull(elp.basis) !== null
  );
  const hasShortCallCredits = numOrNull(row.leaps.total_short_call_credits) !== null;
  return !!row.symbol
    && (hasPosition || hasShortCallCredits)
    && !!row.leaps.leaps_assignment_policy;
}

// ── Provider ────────────────────────────────────────────────────

const METADATA_SOURCE = "operator_manual";

function buildMetadata(extra) {
  const nowIso = new Date().toISOString();
  return {
    context_created_at: extra?.context_created_at || nowIso,
    context_updated_at: nowIso,
    source: METADATA_SOURCE,
    read_only: true,
    execution_enabled: false,
    journal_mutation_enabled: false,
    broker_execution_enabled: false,
  };
}

export function CFMInventoryProvider({ children }) {
  const [rows, setRows] = useState({});
  const [metadata, setMetadata] = useState(null);

  function bump(nextRows) {
    setRows(nextRows);
    setMetadata(prev => buildMetadata(prev));
  }

  function upsertRow(symbol) {
    const sym = (symbol || "").trim().toUpperCase();
    if (!sym) return;
    if (rows[sym]) return; // already exists
    bump({ ...rows, [sym]: defaultRow(sym) });
  }

  function updateField(symbol, fieldPath, value) {
    const sym = (symbol || "").toUpperCase();
    const row = rows[sym];
    if (!row) return;
    const next = { ...row };
    // fieldPath is dot-delimited e.g. "shares.share_basis" or
    // "leaps.existing_leaps_position.basis".
    const parts = fieldPath.split(".");
    let cursor = next;
    for (let i = 0; i < parts.length - 1; i++) {
      const k = parts[i];
      cursor[k] = { ...(cursor[k] || {}) };
      cursor = cursor[k];
    }
    cursor[parts[parts.length - 1]] = value;
    bump({ ...rows, [sym]: next });
  }

  function updateLeapsField(symbol, fieldPath, value) {
    return updateField(symbol, `leaps.${fieldPath}`, value);
  }

  function removeRow(symbol) {
    const sym = (symbol || "").toUpperCase();
    if (!rows[sym]) return;
    const next = { ...rows };
    delete next[sym];
    bump(next);
  }

  function clearAll() {
    setRows({});
    setMetadata(null);
  }

  function importFromJson(text) {
    let parsed;
    try { parsed = JSON.parse(text || ""); }
    catch (_e) { return { ok: false, error: "Invalid JSON" }; }
    const incoming = parsed?.rows && typeof parsed.rows === "object"
      ? parsed.rows : (typeof parsed === "object" ? parsed : null);
    if (!incoming) return { ok: false, error: "Unsupported shape" };
    const next = {};
    Object.entries(incoming).forEach(([sym, row]) => {
      const upper = (sym || "").toUpperCase();
      const base = defaultRow(upper);
      next[upper] = {
        symbol: upper,
        shares: { ...base.shares, ...(row?.shares || {}) },
        leaps:  {
          ...base.leaps,
          ...(row?.leaps || {}),
          existing_leaps_position: {
            ...base.leaps.existing_leaps_position,
            ...((row?.leaps || {}).existing_leaps_position || {}),
          },
        },
      };
    });
    bump(next);
    return { ok: true, count: Object.keys(next).length };
  }

  function exportEnvelope() {
    return {
      cfm_inventory_context_version: "phase3g_cfm_inventory_v1",
      metadata: metadata || buildMetadata(null),
      rows,
    };
  }

  const value = useMemo(() => ({
    rows, metadata,
    upsertRow, updateField, updateLeapsField,
    removeRow, clearAll, importFromJson, exportEnvelope,
  }), [rows, metadata]);

  return (
    <CFMInventoryContext.Provider value={value}>
      {children}
    </CFMInventoryContext.Provider>
  );
}

export function useCFMInventory() {
  return useContext(CFMInventoryContext);
}
